import { CloseOutlined, WarningFilled } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Radio,
  Row,
  Typography,
} from 'antd';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import { Phone } from '@app/components';
import { CommentButton } from '@app/components/CommentButton';
import { phoneValidateHandler } from '@app/components/Phone/utils';
import { CertificateSaleItems } from '@app/components/RecordModal/components/RecordDetails/components/CertificateSaleItems';
import { ClientImportantFields } from '@app/components/RecordModal/components/RecordDetails/components/ClientImportantFields';
import { ClientSubscriptions } from '@app/components/RecordModal/components/RecordDetails/components/ClientSubscriptions';
import { SubscriptionSaleItems } from '@app/components/RecordModal/components/RecordDetails/components/SubscriptionSaleItems';
import {
  clientStatuses,
  mainFields,
  PF,
  recordTypesPart1,
  recordTypesPart2,
} from '@app/components/RecordModal/components/RecordDetails/data/constants';
import {
  useClientSubscriptionServices,
  useCommentFieldSize,
  useRecordDetails,
  useRecordDetailsEffects,
  useSubscriptionQuantityLimits,
} from '@app/components/RecordModal/components/RecordDetails/hooks';
import {
  Statuses,
  Services,
  ProductSaleItems,
  Employee,
  PaymentButton,
  ClientSearch,
  EmployeeTabs,
  PhoneVerifiedToggle,
  PhoneClientMenu,
} from '@app/components/RecordModal/components/RecordDetails/ui';
import { getValidationFieldLabel } from '@app/components/RecordModal/components/RecordDetails/utils/validationFieldLabels';
import type { ContentPropsType } from '@app/components/RecordModal/types';
import { MenuTabEnum } from '@app/components/RecordModal/types';
import { WithTooltip } from '@app/components/WithTooltip';
import { OFF, ONE_TIME_CODE } from '@app/constants';

import { CommentModal } from './CommentModal';
import { NotificationModal } from './NotificationModal';

import '@app/components/RecordModal/components/RecordDetails/styles/RecordDetails.desktop.scss';
import '@app/components/RecordModal/components/RecordDetails/styles/RecordDetails.tablet.scss';
import '@app/components/RecordModal/components/RecordDetails/styles/RecordDetails.mobile.scss';

const { Item } = Form;
const { Text, Title } = Typography;
const { TextArea } = Input;

export const RecordDetails: ContentPropsType = ({
  onCancel,
  onSelectTab,
  confirmUnsavedData,
  confirmWarnings,
}) => {
  const {
    form,
    formValues,
    onSelectStatus,
    checkDisabledStatus,
    onFinish,
    showPaymentButton,
    showClientSearch,
    clientSearchLockReason,
    tagOptions,
    initForm,
    resetForm,
    toggleLock,
    importantEmployeeHandler,
    serviceOptions,
    clientSearchTerm,
    onChangeClientSearchTerm,
    clientSearchIsLoading,
    clientSearchIsSuccess,
    isClientSearchTooShort,
    currentClientId,
    currentClientName,
    onSelectClient,
    clientSearchResults,
    onChangePhone,
    phoneClients,
    isPhoneClientMenuOpen,
    isPhoneClientSearchLoading,
    isPhoneClientSearchSuccess,
    phoneClientRules,
    onPhoneInputFocus,
    onPhoneInputBlur,
    onPhoneCountryListOpenChange,
    onSelectPhoneClient,
    restoreSavedClientFields,
    handleConfirm,
    isLoading,
    isOpen,
    totalDuration,
    formRef,
    onConfirm,
    checkUnsavedData,
    isInitialized,
    hasChanges,
    showDeleteButton,
    isOpenDeleteRecordModal,
    closeDeleteRecordModal,
    openDeleteRecordModal,
    onDeleteRecord,
    isDeleteRecordLoading,
    isOpenErrorModal,
    closeErrorModal,
    errorModalMessage,
    isCommentModal,
    setIsCommentModal,
    clientAllRecords,
    selectedEmployee,
    onSelectEmployee,
    showEmployeeTabs,
    getEmployeesByServicesHandler,
    branchUserOptions,
    warehouses,
    recordActiveBranchId,
    mainEmployeeOptions,
    additinalEmployeeOptions,
    handleDuplicateClick,
    handleAdditionalEmployeeChange,
    handleRemoveAdditionalEmployee,
    handleAddAdditionalEmployee,
    showDoubleRecordButton,
    showDoubleRecordSelects,
    additionalEmployees,
    mainEmployeeSelected,
    containerRef,
    checkRecordTimesHandler,
    setPhoneValidation,
    phoneValidation,
    isSaveRecordButtonDisabled,
    rightToViewContacts,
    EditEmployeePayoff,
    isFormDisabled,
    employeePayoffHandler,
    onSelectEmployeeForService,
    isFetchingGetClientServicesByEmployeeId,
    isLoadingGetClientServicesByEmployeeId,
    tags,
    setTags,
    getAllTagsHandler,
    allTags,
    closeRecordDetails,
    isLoyaltyApplied,
    recordId,
    recordType,
    savedServices,
  } = useRecordDetails({ confirmUnsavedData, confirmWarnings });

  const commentFieldSize = useCommentFieldSize();
  const { appliedClientSubscriptionIds, toggleClientSubscriptionHandler } =
    useClientSubscriptionServices({ form, serviceOptions });
  const { getSubscriptionQuantityLimit } = useSubscriptionQuantityLimits({
    clientId: currentClientId,
    activeBranchId: recordActiveBranchId,
    savedServices,
  });
  const isServicesDisabled =
    isLoading || isFormDisabled || Boolean(recordId && isLoyaltyApplied);

  // До первого «Сохранить» поля не проверяются; после неудачной попытки
  // валидация переходит в реальное время — ошибки появляются и гаснут по
  // мере правки, тултип показывает актуальный текст (доработка us/10653).
  const [isRealtimeValidation, setIsRealtimeValidation] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsRealtimeValidation(false);
    }
  }, [isOpen]);

  // Ошибка телефона ставится вручную через setFields и сама не гаснет —
  // в realtime-режиме синхронизируем её с результатом валидации Phone.
  useEffect(() => {
    if (isRealtimeValidation) {
      void phoneValidateHandler(form, phoneValidation);
    }
  }, [isRealtimeValidation, phoneValidation, form]);

  useRecordDetailsEffects({
    initForm,
    resetForm,
    importantEmployeeHandler,
    isOpen,
    handleConfirm,
    checkUnsavedData,
    isInitialized,
    hasChanges,
    getEmployeesByServicesHandler,
    checkRecordTimesHandler,
    employeePayoffHandler,
    getAllTagsHandler,
    closeRecordDetails,
    restoreSavedClientFields,
  });

  return (
    <div className={`${PF}__wrapper`} ref={containerRef}>
      <Form
        ref={formRef}
        form={form}
        className={`${PF}__form`}
        clearOnDestroy
        autoComplete={OFF}
        layout="vertical"
        scrollToFirstError
        onFinish={onFinish}
        disabled={isFormDisabled}
        // Поля не валидируются на вводе до первой попытки сохранить: все
        // проверки запускает клик «Сохранить» (submit прогоняет правила
        // независимо от триггеров). После неудачной попытки — onChange.
        validateTrigger={isRealtimeValidation ? 'onChange' : []}
        onFinishFailed={(errorInfo) => {
          setIsRealtimeValidation(true);
          void phoneValidateHandler(form, phoneValidation);

          const validationMessages = [
            ...(phoneValidation.errorMessage || !phoneValidation.isValid
              ? [
                  `Мобильный телефон - ${
                    phoneValidation.errorMessage || 'Обязательное поле'
                  }`,
                ]
              : []),
            ...errorInfo.errorFields.flatMap(({ name, errors }) =>
              errors.map(
                (errorText) => `${getValidationFieldLabel(name)} - ${errorText}`
              )
            ),
          ];

          if (validationMessages.length > 0) {
            // key заменяет предыдущую сводку при повторном клике вместо
            // стопки одинаковых сообщений.
            void message.error({
              key: 'record-details-validation',
              content: (
                <div className={`${PF}__validation-summary`}>
                  <div className={`${PF}__validation-summary-title`}>
                    Запись не сохранена — проверьте поля:
                  </div>
                  <ul className={`${PF}__validation-summary-list`}>
                    {validationMessages.map((validationMessage) => (
                      <li key={validationMessage}>{validationMessage}</li>
                    ))}
                  </ul>
                </div>
              ),
            });
          }

          const firstErrorName = errorInfo.errorFields?.[0]?.name;

          requestAnimationFrame(() => {
            if (firstErrorName) {
              form.scrollToField(firstErrorName, {
                behavior: 'smooth',
                block: 'center',
              });
              return;
            }

            if (!phoneValidation.isValid) {
              form.scrollToField(['Phone'], {
                behavior: 'smooth',
                block: 'center',
              });
            }
          });
        }}
      >
        <div className={`${PF}__details`}>
          <div className={`${PF}__main`}>
            <Item name="Status" noStyle>
              <Statuses
                selectedItem={formValues?.Status ?? null}
                onSelect={onSelectStatus}
                items={clientStatuses}
                checkDisabledStatus={checkDisabledStatus}
                disabled={isLoading || isFormDisabled}
              />
            </Item>
            <Divider />
            <div className={`${PF}__main-fields`}>
              {showClientSearch && (
                <div className={`${PF}__main-fields-item`}>
                  <Text className={`${PF}__main-fields-label`}>Поиск</Text>
                  <Item className={`${PF}__main-fields-input`}>
                    <ClientSearch
                      searchValue={clientSearchTerm}
                      clients={clientSearchResults}
                      clientId={currentClientId}
                      clientName={currentClientName}
                      isLoading={clientSearchIsLoading}
                      isSuccess={clientSearchIsSuccess}
                      isTooShort={isClientSearchTooShort}
                      onSearch={onChangeClientSearchTerm}
                      onSelect={onSelectClient}
                      disabled={
                        isLoading ||
                        !rightToViewContacts ||
                        isFormDisabled ||
                        !!clientSearchLockReason
                      }
                      lockReason={clientSearchLockReason}
                    />
                  </Item>
                </div>
              )}
              <div className={`${PF}__main-fields-item phone-info`}>
                <Text
                  className={cn(`${PF}__main-fields-label`, {
                    'label-require': true,
                  })}
                  data-testid={'test'}
                >
                  Мобильный телефон
                </Text>
                <Item className={`${PF}__main-fields-input`}>
                  <div className={`${PF}__main-fields-phone`}>
                    <Item
                      name="IsPhoneVerified"
                      valuePropName="checked"
                      noStyle
                    >
                      <PhoneVerifiedToggle
                        disabled={isLoading || isFormDisabled}
                      />
                    </Item>
                    {/* Номер ищет клиента (task/11629). У записи с проданным
                        абонементом клиент не меняется: поле закрыто, как
                        «Поиск». */}
                    <WithTooltip
                      show={!!clientSearchLockReason}
                      title={clientSearchLockReason}
                    >
                      <PhoneClientMenu
                        clients={phoneClients}
                        open={isPhoneClientMenuOpen}
                        isLoading={isPhoneClientSearchLoading}
                        isSuccess={isPhoneClientSearchSuccess}
                        onSelect={onSelectPhoneClient}
                      >
                        <Item
                          name="Phone"
                          rules={phoneClientRules}
                          style={{ width: '100%' }}
                        >
                          <Phone
                            name="Phone"
                            required
                            disabled={isLoading}
                            onValidate={setPhoneValidation}
                            onFinish={onChangePhone}
                            onFocus={onPhoneInputFocus}
                            onBlur={onPhoneInputBlur}
                            onCountryListOpenChange={
                              onPhoneCountryListOpenChange
                            }
                            readOnly={
                              isFormDisabled || !!clientSearchLockReason
                            }
                          />
                        </Item>
                      </PhoneClientMenu>
                    </WithTooltip>
                  </div>
                </Item>
              </div>

              {mainFields.map(
                ({
                  label,
                  rules,
                  required,
                  name,
                  labelTestid,
                  inputTestid,
                  maxLength,
                }) => (
                  <div key={name} className={`${PF}__main-fields-item`}>
                    <Text
                      className={cn(`${PF}__main-fields-label`, {
                        'label-require': required,
                      })}
                      data-testid={labelTestid}
                    >
                      {label}
                    </Text>
                    <Item
                      name={name}
                      rules={rules}
                      className={`${PF}__main-fields-input`}
                    >
                      <Input
                        maxLength={maxLength}
                        autoComplete={ONE_TIME_CODE}
                        size="large"
                        disabled={
                          isLoading || !rightToViewContacts || isFormDisabled
                        }
                        data-testid={inputTestid}
                      />
                    </Item>
                  </div>
                )
              )}
              <ClientImportantFields
                clientId={currentClientId}
                isDisabled={isLoading || isFormDisabled}
              />
              <div
                className={`${PF}__main-fields-item ${PF}__main-fields-item--textarea mt-12`}
              >
                <Text
                  className={`${PF}__main-fields-label`}
                  data-testid="comment-label"
                >
                  Комментарий
                </Text>
                <Item name="Comment" noStyle>
                  <TextArea
                    className={`${PF}__comment-textarea`}
                    data-testid="comment-input"
                    {...commentFieldSize}
                    maxLength={5000}
                    placeholder="Введите комментарий"
                    size="large"
                    autoComplete={ONE_TIME_CODE}
                    disabled={isLoading || isFormDisabled}
                  />
                </Item>
              </div>
              {/* Тип записи считает бэк (us/11144): у существующей записи он
                  только показывается, выбрать другой нельзя (issue/11350). */}
              {recordType && (
                <div
                  className={`${PF}__main-fields-item mt-12`}
                  data-testid="record-type"
                >
                  <Text className={`${PF}__main-fields-label`}>Тип записи</Text>
                  <Radio.Group
                    value={recordType}
                    size="middle"
                    className="full-width"
                    disabled
                  >
                    <Row>
                      {recordTypesPart1.map(({ value, label }) => (
                        <Col span={8} key={value}>
                          <Radio value={value}>{label}</Radio>
                        </Col>
                      ))}
                    </Row>
                    <Row>
                      {recordTypesPart2.map(({ value, label }) => (
                        <Col span={8} key={value}>
                          <Radio value={value}>{label}</Radio>
                        </Col>
                      ))}
                    </Row>
                  </Radio.Group>
                </div>
              )}
            </div>
          </div>
          <div className={`${PF}__search`}>
            <CommentButton
              setIsCommentModal={setIsCommentModal}
              className={`${PF}__comment-button`}
            />
          </div>
        </div>
        <Divider />
        {showEmployeeTabs && (
          <EmployeeTabs
            employees={clientAllRecords}
            selectedEmployee={selectedEmployee}
            onSelectEmployee={onSelectEmployee}
            maxWidth={containerRef?.current?.clientWidth || null}
            disabled={isLoading || isFormDisabled}
          />
        )}
        <div className={`${PF}__services`}>
          {/* Абонементы клиента — над «Услугами», как в прототипе us/11427:
              клик подставляет услуги абонемента в таблицу ниже. */}
          <ClientSubscriptions
            clientId={currentClientId}
            activeBranchId={recordActiveBranchId}
            appliedClientSubscriptionIds={appliedClientSubscriptionIds}
            onToggle={toggleClientSubscriptionHandler}
            disabled={isServicesDisabled}
          />
          <Title
            level={5}
            data-testid="services-title"
            className={`${PF}__services-title`}
          >
            Услуги
          </Title>
          <Services
            totalDuration={totalDuration}
            serviceOptions={serviceOptions}
            branchUserOptions={branchUserOptions}
            getSubscriptionQuantityLimit={getSubscriptionQuantityLimit}
            disabled={isServicesDisabled}
          />
        </div>
        <div className={`${PF}__services`}>
          <Title
            level={5}
            data-testid="product-sale-items-title"
            className={`${PF}__services-title`}
          >
            Товары
          </Title>
          <Item
            name="products"
            valuePropName="items"
            trigger="onChange"
            noStyle
          >
            <ProductSaleItems
              warehouses={warehouses}
              branchUserOptions={branchUserOptions}
              activeBranchId={recordActiveBranchId}
              disabled={isLoading || isFormDisabled}
              items={formValues?.products ?? []}
              onChange={() => {}}
            />
          </Item>
        </div>
        {/* Продажа сертификата (прототип dev.promo-grushka.ru) — пока только
            UI: методов загрузки и сохранения сертификатов записи на бэкенде
            ещё нет, строки на бэкенд не уходят. */}
        <div className={`${PF}__services`}>
          <Title
            level={5}
            data-testid="certificate-sale-items-title"
            className={`${PF}__services-title`}
          >
            Сертификаты
          </Title>
          <Item
            name="certificates"
            valuePropName="items"
            trigger="onChange"
            noStyle
          >
            <CertificateSaleItems
              branchUserOptions={branchUserOptions}
              activeBranchId={recordActiveBranchId}
              disabled={isLoading || isFormDisabled}
            />
          </Item>
        </div>
        <div className={`${PF}__services`}>
          <Title
            level={5}
            data-testid="subscription-sale-items-title"
            className={`${PF}__services-title`}
          >
            Абонементы
          </Title>
          <Item
            name="subscriptions"
            valuePropName="items"
            trigger="onChange"
            noStyle
          >
            <SubscriptionSaleItems
              branchUserOptions={branchUserOptions}
              activeBranchId={recordActiveBranchId}
              disabled={isLoading || isFormDisabled}
            />
          </Item>
        </div>
        <Divider />
        <Employee
          mainEmployeeOptions={mainEmployeeOptions}
          tagOptions={tagOptions}
          toggleLock={toggleLock}
          disabled={isLoading || isSaveRecordButtonDisabled || isFormDisabled}
          showDoubleRecordButton={showDoubleRecordButton}
          showDoubleRecordSelects={showDoubleRecordSelects}
          handleAddAdditionalEmployee={handleAddAdditionalEmployee}
          handleRemoveAdditionalEmployee={handleRemoveAdditionalEmployee}
          handleAdditionalEmployeeChange={handleAdditionalEmployeeChange}
          handleDuplicateClick={handleDuplicateClick}
          additinalEmployeeOptions={additinalEmployeeOptions}
          additionalEmployees={additionalEmployees}
          mainEmployeeSelected={mainEmployeeSelected}
          showEmployeePayoff={EditEmployeePayoff}
          isFormDisabled={isFormDisabled}
          onSelectEmployee={onSelectEmployeeForService}
          isFetchingGetClientServicesByEmployeeId={
            isFetchingGetClientServicesByEmployeeId
          }
          isLoadingGetClientServicesByEmployeeId={
            isLoadingGetClientServicesByEmployeeId
          }
          tags={tags}
          setTags={setTags}
          allTags={allTags}
        />
        {showPaymentButton && (
          <PaymentButton
            onClick={() => onSelectTab(MenuTabEnum.RecordPayment)}
          />
        )}
      </Form>
      <div className={`${PF}__footer`}>
        {showDeleteButton && (
          <Button
            onClick={openDeleteRecordModal}
            size="large"
            danger
            className={`${PF}__footer-button`}
            data-testid="delete-record"
            disabled={isLoading || isFormDisabled}
          >
            Удалить
          </Button>
        )}
        <Button
          type="default"
          size="large"
          className={`${PF}__footer-button`}
          onClick={onCancel}
          disabled={isLoading || isFormDisabled}
          data-testid="cancel"
        >
          Отмена
        </Button>
        <Button
          type="primary"
          size="large"
          data-testid="save"
          className={`${PF}__footer-button ${PF}__footer-button-save`}
          // Пока идёт поиск клиента по номеру, сохранить нельзя (task/11629).
          loading={isPhoneClientSearchLoading}
          onClick={() => {
            if (phoneValidation.errorMessage) {
              form.scrollToField(['Phone'], {
                behavior: 'smooth',
                block: 'center',
              });

              return;
            }
            onConfirm();
          }}
          disabled={isLoading || isSaveRecordButtonDisabled || isFormDisabled}
        >
          Сохранить
        </Button>
      </div>
      <NotificationModal
        okText="Удалить"
        cancelText="Отменить"
        title={'Удалить запись из базы.'}
        showCancelButton={true}
        isModalOpen={isOpenDeleteRecordModal}
        handleSubmit={onDeleteRecord}
        handleCancel={closeDeleteRecordModal}
        confirmLoading={isDeleteRecordLoading}
        content={'Вы уверены, что хотите удалить запись из базы?'}
        okButtonProps={{
          type: 'primary',
          danger: true,
          'data-testid': 'save',
        }}
        dataTestId="delete-record-popup"
        customCloseIcon={<CloseOutlined data-testid="close-popup" />}
        titleDataTestId="delete-record-title"
        contentDataTestId="delete-record-description"
        cancelButtonProps={{
          'data-testid': 'cancel',
        }}
      />
      <NotificationModal
        okText="OK"
        isModalOpen={isOpenErrorModal}
        title={
          <>
            <WarningFilled className="notification" /> Произошла ошибка
          </>
        }
        showCancelButton={false}
        handleSubmit={closeErrorModal}
        handleCancel={closeErrorModal}
        content={errorModalMessage}
      />
      <CommentModal
        isCommentModal={isCommentModal}
        setIsCommentModal={setIsCommentModal}
      />
    </div>
  );
};
